import {
    SHOP_NAVIGATION_VARIANTS,
    type ShopNavigationVariant,
} from "../shopVerticalCatalog";
import {
    SITE_NAVIGATION_VARIANT_OPTION,
    normalizeSiteNavigationVariant,
} from "../siteNavigation";
import type { ResolvedTemplateDesign, ShopTemplateBlock } from "../types";

/** @deprecated Prefer SITE_NAVIGATION_VARIANT_OPTION. */
export const LAGUNA_NAVIGATION_VARIANT_OPTION = SITE_NAVIGATION_VARIANT_OPTION;

export const LAGUNA_NAVIGATION_VARIANTS = SHOP_NAVIGATION_VARIANTS;

export type LagunaNavigationVariant = ShopNavigationVariant;

const lagunaNavigationVariantSet = new Set<string>(LAGUNA_NAVIGATION_VARIANTS);

export function isLagunaNavigationVariant(value: unknown): value is LagunaNavigationVariant {
    return typeof value === "string" && lagunaNavigationVariantSet.has(value);
}

export function defaultLagunaNavigationVariant(
    _headerVariant: ResolvedTemplateDesign["components"]["header"] | null | undefined,
): LagunaNavigationVariant {
    return "lagunaClassic";
}

export function normalizeLagunaNavigationVariant(
    value: unknown,
    fallback: LagunaNavigationVariant = "lagunaClassic",
): LagunaNavigationVariant {
    const normalized = normalizeSiteNavigationVariant(value, fallback);

    return isLagunaNavigationVariant(normalized) ? normalized : fallback;
}

export function findLagunaHeaderMenuBlock(block: ShopTemplateBlock | null | undefined): ShopTemplateBlock | null {
    if (!block) {
        return null;
    }

    if (block.type === "headerMenu" || block.options?.documentRole === "headerMenu") {
        return block;
    }

    for (const children of Object.values(block.slots ?? {})) {
        for (const child of children) {
            const match = findLagunaHeaderMenuBlock(child);

            if (match) {
                return match;
            }
        }
    }

    for (const child of block.children ?? []) {
        const match = findLagunaHeaderMenuBlock(child);

        if (match) {
            return match;
        }
    }

    return null;
}

/**
 * Stores one navigation variant on the canonical header-menu block without
 * changing the surrounding authored document. If the supplied block is a
 * header root, only the first matching menu descendant is replaced; if no
 * menu exists, the original object is returned unchanged.
 */
export function withLagunaNavigationVariant(
    block: ShopTemplateBlock,
    variant: LagunaNavigationVariant,
): ShopTemplateBlock {
    if (block.type === "headerMenu" || block.options?.documentRole === "headerMenu") {
        if (block.options?.[LAGUNA_NAVIGATION_VARIANT_OPTION] === variant) {
            return block;
        }

        return {
            ...block,
            options: {
                ...(block.options ?? {}),
                [LAGUNA_NAVIGATION_VARIANT_OPTION]: variant,
            },
        };
    }

    for (const [slotKey, slotBlocks] of Object.entries(block.slots ?? {})) {
        for (let index = 0; index < slotBlocks.length; index += 1) {
            const child = slotBlocks[index];

            if (!child || !findLagunaHeaderMenuBlock(child)) {
                continue;
            }

            const nextChild = withLagunaNavigationVariant(child, variant);

            return nextChild === child
                ? block
                : {
                    ...block,
                    slots: {
                        ...(block.slots ?? {}),
                        [slotKey]: slotBlocks.map((candidate, childIndex) => (
                            childIndex === index ? nextChild : candidate
                        )),
                    },
                };
        }
    }

    for (let index = 0; index < (block.children?.length ?? 0); index += 1) {
        const child = block.children?.[index];

        if (!child || !findLagunaHeaderMenuBlock(child)) {
            continue;
        }

        const nextChild = withLagunaNavigationVariant(child, variant);

        return nextChild === child
            ? block
            : {
                ...block,
                children: block.children?.map((candidate, childIndex) => (
                    childIndex === index ? nextChild : candidate
                )),
            };
    }

    return block;
}

export function resolveLagunaNavigationVariant(
    headerBlock: ShopTemplateBlock | null | undefined,
    headerVariant: ResolvedTemplateDesign["components"]["header"] | null | undefined,
): LagunaNavigationVariant {
    const fallback = defaultLagunaNavigationVariant(headerVariant);
    const menuBlock = findLagunaHeaderMenuBlock(headerBlock);

    return normalizeLagunaNavigationVariant(
        menuBlock?.options?.[LAGUNA_NAVIGATION_VARIANT_OPTION],
        fallback,
    );
}
